<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.ACTIVITIES.EXTERNAL_BILL_OF_LADING") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
      </template>

      <v-card v-show="!isPageLoading">
        <SearchTools
          :searchQuery.sync="searchQuery"
          @do-search="doSearch"
          @do-clear="doClear"
          :placeholder="$t('LABELS.SEARCH')"
        >
          <template v-slot:prepend>
            <b-col class="pb-0" :md="6">
              <b-form-group id="date-from-group" label-for="date-from-input">
                <label for="date-from-input">{{
                  $t("FORM_LABELS.DATE_FROM")
                }}</label>
                <FormDatepicker
                  id="date-from-input"
                  v-model="filters.dateFrom"
                  @input="doSearch"
                />
              </b-form-group>
            </b-col>
            <b-col class="pb-0" :md="6">
              <b-form-group id="date-to-group" label-for="date-to-input">
                <label for="date-to-input">{{
                  $t("FORM_LABELS.DATE_TO")
                }}</label>
                <FormDatepicker
                  id="date-to-input"
                  v-model="filters.dateTo"
                  @input="doSearch"
                />
              </b-form-group>
            </b-col>
          </template>
        </SearchTools>

        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isLoadingExternalBillOfLading"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          @update:options="getDataFromApi()"
          class="px-6 elevation-1"
        >
          <template v-slot:[`item.machine`]="{ item }">
            {{ item.machine.registrationPlate }}
          </template>

          <template v-slot:[`item.date`]="{ item }">
            {{ item.date | moment("DD.MM.YYYY") }}
          </template>

          <template v-slot:[`item.price`]="{ item }">
            {{ item.price | currency }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['bill-of-lading-update'])"
              :to="{
                name: 'edit-external-bill-of-lading',
                params: { id: item.id }
              }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mr-2" fab dark x-small v-bind="attrs" v-on="on">
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.EDIT") }}</span>
              </v-tooltip>
            </router-link>
            <template v-else>{{ $t("LABELS.NO_PERMISSIONS") }}</template>
          </template>

          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              {{ $t("VALIDATION.NO_RESULTS") }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { mapGetters } from "vuex";

import SearchTools from "@/core/components/table/SearchTools.vue";
import FormDatepicker from "@/view/content/forms/components/FormDatepicker.vue";
import FormSelect from "@/view/content/forms/components/FormSelect.vue";

import { FETCH_EXTERNAL_BILL_OF_LADINGS } from "../store/external-bill-of-lading.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

export default {
  name: "ExternalBillOfLadingList",
  mixins: [permissionMixin],
  components: {
    SearchTools,
    FormSelect,
    FormDatepicker
  },
  data() {
    return {
      isPageLoading: true,
      items: [],
      totalItems: 0,
      searchQuery: "",
      options: {
        sortBy: ["date"],
        sortDesc: [true],
        page: 1,
        itemsPerPage: 30
      },
      filters: {
        dateFrom: null,
        dateTo: null
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      headers: [
        {
          text: this.$i18n.t("MENU.ITEM.ACTIVITIES.BILL_OF_LADING_DATE"),
          value: "date"
        },
        {
          text: this.$i18n.t("LABELS.BILL_NUMBER"),
          value: "billNumber"
        },
        {
          text: this.$i18n.t("LABELS.MACHINE"),
          value: "machine",
          sortable: false
        },
        {
          text: this.$i18n.t("LABELS.SERVICE_NAME"),
          value: "serviceName",
          sortable: false
        },
        {
          text: this.$i18n.t("LABELS.LOCATION"),
          value: "location",
          sortable: false
        },
        {
          text: this.$i18n.t("LABELS.WORK_DONE"),
          value: "workDone"
        },
        {
          text: this.$i18n.t("FORM_LABELS.PRICE"),
          value: "price",
          align: "right"
        },
        {
          align: "right",
          text: this.$i18n.t("MENU.ITEM.ACTIONS"),
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ],
      assignors: []
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.ACTIVITIES.EXTERNAL_BILL_OF_LADING"),
        route: { name: "list-external-bill-of-lading" }
      },
      { title: this.$i18n.t("LABELS.LIST") }
    ]);
  },
  computed: {
    ...mapGetters(["isLoadingExternalBillOfLading"]),
    params() {
      return {
        ...this.options,
        ...this.filters,
        query: this.searchQuery
      };
    },
    tableHeaders() {
      return this.headers;
    }
  },
  methods: {
    doSearch() {
      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },

    doClear() {
      this.searchQuery = "";
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = null;
      });

      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },

    getDataFromApi() {
      let params = this.params;

      params.length = params.itemsPerPage;
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1);

      let apiParams = this.$url.transformParams(params);

      this.$store
        .dispatch(FETCH_EXTERNAL_BILL_OF_LADINGS, apiParams)
        .then(data => {
          this.$nextTick(() => {
            this.items = data.data.items;
            this.totalItems = data.data.totalItemsCount;

            if (this.isPageLoading == true) {
              this.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          if (response.status === 404) {
            this.$notify({
              text: "Not Found",
              type: "error"
            });
          }
        });
    }
  }
};
</script>
